import { useEffect, useState } from "react";
import type { ThemeDocument } from "@/api/queries";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";

/**
 * The brand colour, as one decision (`app/theme/derive.py`).
 *
 * An administrator picks the accent. Its dark-mode counterpart and the ramp —
 * hover, pressed, subtle, text-on-accent — are derived on the server unless
 * set here; so are the selected-row tint, the info tone, chart series one and
 * the heatmap ramp. Advanced still exposes each ramp colour, because a brand
 * guide that names its hover colour is a brand guide that has to be honoured.
 *
 * Empty means "not set here": the value shown is then whatever the server
 * resolved — the file's, or derived — and the chip says which.
 */

export const COLOR_KEYS = [
  "accent",
  "accentHover",
  "accentPressed",
  "accentSubtle",
  "onAccent",
] as const;
export type ColorKey = (typeof COLOR_KEYS)[number];
export type Pair = { light: string; dark: string };
export type ColorForm = Record<ColorKey, Pair>;
type Scheme = keyof Pair;

const RAMP: { key: Exclude<ColorKey, "accent">; label: string; hint: string }[] = [
  { key: "accentHover", label: "Hover", hint: "A primary button under the pointer." },
  { key: "accentPressed", label: "Pressed", hint: "A primary button while it is held." },
  { key: "accentSubtle", label: "Subtle", hint: "Tinted backgrounds: chips, highlights." },
  { key: "onAccent", label: "Text on accent", hint: "Labels on a primary button." },
];

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** `#abc` → `#AABBCC`, which is all `<input type="color">` accepts. */
function full(hex: string): string {
  const value = hex.trim().toUpperCase();
  if (/^#[0-9A-F]{3}$/.test(value)) {
    return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
  }
  return value.slice(0, 7);
}

/**
 * A picker and a hex box over one value. The box keeps its own draft so a
 * half-typed `#7A2` is not sent anywhere; only a whole colour is committed.
 */
function HexInput({
  label,
  value,
  onCommit,
  disabled,
}: {
  label: string;
  value: string;
  onCommit: (next: string) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const invalid = draft.trim() !== "" && !HEX.test(draft.trim());
  return (
    <span className="inline-flex items-center gap-1.5">
      <input
        type="color"
        aria-label={label}
        value={HEX.test(value) ? full(value) : "#000000"}
        disabled={disabled}
        onChange={(event) => onCommit(event.target.value.toUpperCase())}
        className="border-border h-8 w-10 cursor-pointer rounded-inner border bg-transparent p-0.5 disabled:cursor-not-allowed"
      />
      <input
        type="text"
        aria-label={`${label} hex`}
        aria-invalid={invalid || undefined}
        value={draft}
        disabled={disabled}
        spellCheck={false}
        onChange={(event) => {
          setDraft(event.target.value);
          // Six digits commit as typed; three only on leaving the box, or
          // typing `#7A2E3B` would preview `#77AA22` on the way past `#7A2`.
          if (/^#[0-9a-fA-F]{6}$/.test(event.target.value.trim())) onCommit(full(event.target.value));
        }}
        onBlur={() => {
          if (HEX.test(draft.trim()) && full(draft) !== value.toUpperCase()) onCommit(full(draft));
          else setDraft(value);
        }}
        className={
          "bg-card text-ink rounded-element type-code w-24 border px-2 py-1 " +
          (invalid ? "border-error" : "border-border")
        }
      />
    </span>
  );
}

function Origin({ set, derived }: { set: boolean; derived: string }) {
  return set ? <Chip tone="accent">set here</Chip> : <Chip>{derived}</Chip>;
}

function SchemeColor({
  label,
  scheme,
  chosen,
  effective,
  derived,
  disabled,
  onChange,
}: {
  label: string;
  scheme: Scheme;
  chosen: string;
  effective: string;
  derived: string;
  disabled?: boolean;
  onChange: (next: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="type-caption text-ink-disabled w-10">{scheme}</span>
      <HexInput
        label={`${label} (${scheme} mode)`}
        value={chosen || effective}
        disabled={disabled}
        onCommit={onChange}
      />
      <Origin set={Boolean(chosen)} derived={derived} />
      {chosen ? (
        <Button size="sm" variant="ghost" disabled={disabled} onClick={() => onChange("")}>
          {derived === "from tokens.json" ? "Use the file's" : "Use automatic"}
        </Button>
      ) : null}
    </div>
  );
}

export function ColorEditor({
  form,
  document,
  disabled,
  onChange,
}: {
  form: ColorForm;
  /** The resolved (preview) document: what every unset value currently is. */
  document: ThemeDocument;
  disabled?: boolean;
  onChange: (next: ColorForm) => void;
}) {
  const effective = document.brand.color as Record<string, Pair | undefined>;
  const set = (key: ColorKey, scheme: Scheme, value: string) =>
    onChange({ ...form, [key]: { ...form[key], [scheme]: value } });
  // Open on arrival when something in it is already set; after that it is the
  // reader's to open and close — a constant prop React never re-applies.
  const [advancedOpen] = useState(() => RAMP.some(({ key }) => form[key].light || form[key].dark));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div>
          <p className="type-label text-ink">Brand colour</p>
          <p className="type-support text-ink-secondary">
            Buttons, links, the selected row, the info tone, chart series one and every heatmap
            follow it. Pick the light-mode colour; the dark-mode one is worked out to read on a
            dark background unless you set it.
          </p>
        </div>
        <SchemeColor
          label="Brand colour"
          scheme="light"
          chosen={form.accent.light}
          effective={effective.accent?.light ?? ""}
          derived="from tokens.json"
          disabled={disabled}
          onChange={(value) => set("accent", "light", value)}
        />
        <SchemeColor
          label="Brand colour"
          scheme="dark"
          chosen={form.accent.dark}
          effective={effective.accent?.dark ?? ""}
          derived={form.accent.light ? "automatic" : "from tokens.json"}
          disabled={disabled}
          onChange={(value) => set("accent", "dark", value)}
        />
      </div>

      <details className="border-border rounded-element border p-3" open={advancedOpen}>
        <summary className="type-label text-ink cursor-pointer select-none">
          Advanced — the rest of the ramp
        </summary>
        <p className="type-support text-ink-secondary mt-2">
          Automatic unless your brand guide names them. Each one you set here wins over the
          derived value; "Use automatic" goes back.
        </p>
        <div className="mt-3 flex flex-col gap-4">
          {RAMP.map(({ key, label, hint }) => (
            <div key={key} className="flex flex-col gap-1.5">
              <div>
                <span className="type-label text-ink">{label}</span>
                <span className="type-support text-ink-secondary ms-2">{hint}</span>
              </div>
              {(["light", "dark"] as const).map((scheme) => (
                <SchemeColor
                  key={scheme}
                  label={label}
                  scheme={scheme}
                  chosen={form[key][scheme]}
                  effective={effective[key]?.[scheme] ?? ""}
                  derived="automatic"
                  disabled={disabled}
                  onChange={(value) => set(key, scheme, value)}
                />
              ))}
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}
